import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { GOOGLE_CALENDAR_SUPPORTED_TYPE_NAME } from '../../constants'
import { useAuthWindow } from '../../hooks'
import useRefetchStaleQueries from '../../hooks/useRefetchStaleQueries'
import { useGetCalendars } from '../../services/api/events.hooks'
import Log from '../../services/api/log'
import { useDeleteLinkedAccount, useGetLinkedAccounts, useGetSupportedTypes } from '../../services/api/settings.hooks'
import { useGetUserInfo } from '../../services/api/user-info.hooks'
import { Border, Colors, Spacing, Typography } from '../../styles'
import { icons, logos } from '../../styles/images'
import { TLinkedAccount } from '../../utils/types'
import Flex from '../atoms/Flex'
import { Icon } from '../atoms/Icon'
import { Divider } from '../atoms/SectionDivider'
import GTButton from '../atoms/buttons/GTButton'
import NoStyleButton from '../atoms/buttons/NoStyleButton'
import { BodyLarge, BodyMedium, BodySmall } from '../atoms/typography/Typography'
import BillingSettings from '../billing/BillingSettings'
import CalendarSettings from '../calendar/CalendarSettings'
import { getCalendarAuthButton } from '../calendar/utils/utils'
import GTModal from '../mantine/GTModal'
import DeleteAccountButton from './DeleteAccountButton'
import ExperimentalFeatureSettings from './ExperimentalFeatureSettings'
import SignOutButton from './SignOutButton'

const SERVICE_WIDTH = '150px'
const INTERNAL_DASHBOARD_EMAIL = 'john@johnglomerate.com'

const ServicesContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: ${Spacing._32};
`
const Service = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._12};
    width: ${SERVICE_WIDTH};
`
const ServiceDetails = styled.div`
    ${Typography.body.small};
    color: ${Colors.text.light};
    margin-bottom: auto;
`
const TruncatedLabel = styled(BodySmall)`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`
const SettingsRow = styled(NoStyleButton)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${Spacing._16};
    width: 100%;
    padding: ${Spacing._12};
    border: ${Border.stroke.medium} solid ${Colors.background.border};
    border-radius: ${Border.radius.medium};
    background-color: ${Colors.background.white};
    text-align: left;

    &:hover,
    &:focus {
        background-color: ${Colors.background.sub};
    }

    &:focus-visible {
        outline: ${Border.stroke.large} solid ${Colors.control.secondary.highlight};
        outline-offset: -${Border.stroke.large};
    }
`

// Billing is the third tab below. BillingSettingsLinkHandler opens it from the ?settings=billing link in emails.
export const SETTINGS_BILLING_TAB_INDEX = 2

interface SettingsModalProps {
    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void
    defaultTabIndex?: number
}
const SettingsModal = ({ isOpen, setIsOpen, defaultTabIndex }: SettingsModalProps) => {
    const navigate = useNavigate()
    const { data: userInfo } = useGetUserInfo()
    const { data: supportedTypes } = useGetSupportedTypes(isOpen)
    const { mutate: deleteAccount } = useDeleteLinkedAccount(isOpen)
    const { data: linkedAccounts } = useGetLinkedAccounts(isOpen)
    const { openAuthWindow } = useAuthWindow()
    const { data: calendars } = useGetCalendars(isOpen)

    const refetchStaleQueries = useRefetchStaleQueries(isOpen)

    useEffect(() => {
        if (!isOpen || !linkedAccounts) return
        refetchStaleQueries()
    }, [isOpen, linkedAccounts])

    const onUnlink = (id: string) => {
        Log(`unlink_account_${id}`)
        deleteAccount({ id: id })
    }
    const onRelink = (accountType: string) => {
        Log(`relink_acocount_${accountType}`)
        if (!supportedTypes) return
        for (const type of supportedTypes) {
            if (type.name === accountType) {
                openAuthWindow({ url: type.authorization_url })
                return
            }
        }
    }

    const serviceDetails = {
        'Google Calendar': 'See your upcoming events and schedule tasks by dragging them onto your calendar.',
    }

    const canViewInternalDashboard = userInfo?.email?.toLowerCase() === INTERNAL_DASHBOARD_EMAIL

    const openInternalDashboard = () => {
        setIsOpen(false)
        navigate('/internal')
    }

    const getEnableAllCalendarsButton = (account: TLinkedAccount) => {
        if (account.name !== GOOGLE_CALENDAR_SUPPORTED_TYPE_NAME || !calendars) return
        const calendar = calendars.find((calendar) => calendar.account_id === account.display_id)
        if (!calendar || calendar?.has_multical_scopes) return
        const authUrl = supportedTypes?.find(
            (supportedType) => supportedType.name === GOOGLE_CALENDAR_SUPPORTED_TYPE_NAME
        )?.authorization_url
        if (!authUrl) return

        return getCalendarAuthButton(calendar, () => openAuthWindow({ url: authUrl, isGoogleSignIn: true }))
    }

    return (
        <GTModal
            open={isOpen}
            setIsModalOpen={setIsOpen}
            title="Settings"
            size="lg"
            defaultTabIndex={defaultTabIndex}
            tabs={[
                {
                    title: 'Integrations',
                    icon: icons.globe,
                    body: (
                        <Flex column gap={Spacing._24}>
                            <BodyLarge>Add a new service</BodyLarge>
                            <ServicesContainer>
                                {supportedTypes
                                    ?.sort((a, b) => a.name.localeCompare(b.name))
                                    .map((supportedType) => (
                                        <Service key={supportedType.name}>
                                            <Icon icon={logos[supportedType.logo_v2]} />
                                            <BodyMedium>{supportedType.name}</BodyMedium>
                                            <ServiceDetails>
                                                {serviceDetails[supportedType.name as keyof typeof serviceDetails]}
                                            </ServiceDetails>
                                            <div>
                                                <GTButton
                                                    icon={icons.external_link}
                                                    value={`Connect ${
                                                        supportedType.name === GOOGLE_CALENDAR_SUPPORTED_TYPE_NAME
                                                            ? 'Google'
                                                            : supportedType.name
                                                    }`}
                                                    onClick={() =>
                                                        openAuthWindow({
                                                            url: supportedType.authorization_url,
                                                            isGoogleSignIn:
                                                                supportedType.name ===
                                                                GOOGLE_CALENDAR_SUPPORTED_TYPE_NAME,
                                                        })
                                                    }
                                                    styleType="secondary"
                                                />
                                            </div>
                                        </Service>
                                    ))}
                            </ServicesContainer>
                            <Divider color={Colors.background.border} />
                            <BodyLarge>My integrations</BodyLarge>
                            {linkedAccounts && linkedAccounts.length > 0 ? (
                                linkedAccounts?.map((account) => (
                                    <Flex justifyContent="space-between" alignItems="center" key={account.id}>
                                        <Flex alignItems="center" gap={Spacing._16}>
                                            <Icon icon={logos[account.logo_v2]} />
                                            <Flex column>
                                                <BodySmall>{account.name}</BodySmall>
                                                <BodySmall color="light">{account.display_id}</BodySmall>
                                            </Flex>
                                        </Flex>
                                        <Flex gap={Spacing._8} alignItems="center">
                                            {account.has_bad_token ? (
                                                <GTButton
                                                    onClick={() => onRelink(account.name)}
                                                    value="Re-link account"
                                                    styleType="destructive"
                                                    textColor="red"
                                                />
                                            ) : (
                                                getEnableAllCalendarsButton(account)
                                            )}
                                            {account.is_unlinkable && (
                                                <GTButton
                                                    onClick={() => onUnlink(account.id)}
                                                    value="Disconnect account"
                                                    styleType="secondary"
                                                />
                                            )}
                                        </Flex>
                                    </Flex>
                                ))
                            ) : (
                                <ServiceDetails>
                                    You have no connected integrations. Click from the options above to get started.
                                </ServiceDetails>
                            )}
                        </Flex>
                    ),
                },
                {
                    title: 'Calendar',
                    icon: icons.calendar_blank,
                    body: <CalendarSettings />,
                },
                {
                    title: 'Billing',
                    icon: icons.chartLineUp,
                    body: <BillingSettings />,
                },
                {
                    title: 'Account details',
                    icon: icons.user,
                    body: (
                        <Flex column gap={Spacing._24}>
                            {canViewInternalDashboard && (
                                <>
                                    <SettingsRow onClick={openInternalDashboard}>
                                        <Flex alignItems="center" gap={Spacing._16}>
                                            <Icon icon={icons.chartLineUp} />
                                            <Flex column gap={Spacing._4}>
                                                <BodyLarge>Internal dashboard</BodyLarge>
                                                <BodySmall color="light">Relaunch metrics</BodySmall>
                                            </Flex>
                                        </Flex>
                                        <Icon icon={icons.arrow_right} />
                                    </SettingsRow>
                                    <Divider color={Colors.background.border} />
                                </>
                            )}
                            <Flex column gap={Spacing._12}>
                                <BodySmall color="light">Email</BodySmall>
                                <TruncatedLabel>{userInfo?.email}</TruncatedLabel>
                            </Flex>
                            <div>
                                <SignOutButton />
                            </div>
                            <Divider color={Colors.background.border} />
                            <Flex column gap={Spacing._12}>
                                <BodyLarge>Delete account</BodyLarge>
                                <ServiceDetails>
                                    Permanently delete your account and all of the data we hold for you, including
                                    everything from your connected Google accounts.
                                </ServiceDetails>
                                <div>
                                    <DeleteAccountButton />
                                </div>
                            </Flex>
                        </Flex>
                    ),
                },
                {
                    title: 'Experimental',
                    icon: icons.flask,
                    body: <ExperimentalFeatureSettings />,
                },
            ]}
        />
    )
}

export default SettingsModal
